export * as XML from './xml';

export interface GetReportRequest {
  orderNumbers: number[];
}

export type ReportStatus = 'preliminary' | 'final' | 'unknown';

export interface GetReportResult {
  orderNumber: number;
  status: ReportStatus;
  base64PDFs: string[];
  base64XML: string;
}

export type GetReportResponse = GetReportResult[];

export interface PathologyTestSearchRequest {
  query?: string;
  specialOrder?: boolean;
  commonOnly?: boolean;
  limit?: number;
}

export interface PathologyTestSearchResult {
  description: string;
  code: string;
  department: string;
  /**
   * If the test needs to be on a separate order
   */
  specialOrder: boolean;
  common: boolean;
}

export interface PathologyTestGetRequest {
  codes: string[];
}

export type PathologyTestSearchResponse = PathologyTestSearchResult[];
export type PathologyTestGetResponse = PathologyTestSearchResult[];

export interface StatusQueryResponse {
  Orders: Order[];
}

export interface Order {
  orderNumber?: string;
  orderStatus?: string;
  error?: string;
}

export interface StatusQueryRequest {
  Orders: OrderRequest[];
}

export interface OrderRequest {
  orderNumber: string;
  doctor: string;
}

export interface CancelOrderResponse {
  error?: string;
}

export interface UpdateOrderResponse {
  orderNumber?: string;
  error?: string;
}

export interface Hospital {
  name?: string;
  ward?: string;
  bed?: string;
}

export interface Guarantor {
  idNo: string;
  name: string;
  addressline1: string;
  addressline2?: string;
  addresscity: string;
  cellphoneNo: string;
  /**
   * Required, you can use NA if you don't have a value for this
   */
  homeTelNo: string;
  workTelNo?: string;
  email?: string;
  language?: 'A' | 'E';
  vatNo?: string;
  medicalAidName: string;
  medicalAidNo: string;
  medicalAidDependantCode?: number;
  medicalAidAuthorizationNo?: string;
}

export interface AmpathPatient {
  age?: string;
  dob?: string;
  reference?: string;
  gender: 'M' | 'F' | 'U';
  idNo?: string;
  name: string;
  title?: string;
  otherNo?: string;
  homeTelNo?: string;
  workTelNo?: string;
}

export interface UpdateOrderRequest {
  /**
   * Required if updating an existing order
   */
  orderNumber?: string;
  doctor: string;
  copyDoctors: string[];
  comment?: string;
  /**
   * `YY-MM-DD`
   */
  collectionDate?: string;
  /**
   * `hh:mm:ss`
   */
  collectionTime?: string;
  priority: 'S' | 'R' | 'P';
  informedConsentGiven?: 'Y' | 'N';
  admissionIcd10Code?: string;
  location?: string;
  hospital?: Hospital;
  guarantor: Guarantor;
  patient: AmpathPatient;
  icd10Codes?: string[];
  orderedTests?: string[];
  admissionQuerries: Query[];
  orderQuerries: Query[];
}

export type CreateOrderRequest = Omit<UpdateOrderRequest, 'orderNumber'>;
export type CreateOrderResponse = UpdateOrderRequest;

export interface Query {
  code?: string;
  value?: string;
}
export interface OrderResultQueryRequest {
  orderNumber: number;
  includePDF: boolean;
}

export interface PatientResultQueryRequest {
  identificationNumber: string;
  surname: string;
  /**
   * Cannot request status without consent granted as `true`
   */
  consentGiven: boolean;
  fromDate: string;
  includePDF: boolean;
}

export interface PathologyReport {
  encoding: string;
  /**
   * XML containing results for multiple patient reports
   */
  xml: string;
}

export interface PathologyReportPDF {
  messageId: number;
  /**
   * base64 encoded PDF. Can be converted to a file with:
   *
   * ```js
   * const buff = Buffer.from(pdf, 'base64);
   * fs.writeFileSync('file.pdf', buff);
   * ```
   */
  pdf: string;
}

export interface PatientResultQueryResponse {
  error?: string;
  batchNo: string;
  latestResult: string;
  hasMoreResults: boolean;
  /**
   * This allows us to tell if all tests for the query are fully completed
   */
  allSpecimensComplete: boolean;
  pathologyReports: PathologyReport;
  pathologyReportPDFs: PathologyReportPDF[];
}
